// @flow


import { getCurrentConference } from '../base/conference';
import { setActiveModalId } from '../base/modal';
import { MiddlewareRegistry, StateListenerRegistry } from '../base/redux';
import { getLocalVideoTrack, toggleScreensharing } from '../base/tracks';
import { openEtherPadFunction, toggleDocument, toggleDocumentedit, whichParticipant } from '../etherpad/actions';
import { toggleVisibility } from '../genericiframe';
import { checkParticipant } from '../genericiframe/actions';

import {
    CARMODE_MODAL_ID,
    CLEAR_TOOLBOX_TIMEOUT,
    CLOSE_CARMODE_MODAL,
    OPEN_CARMODE_MODAL,
    OPEN_WHITE_BOARD_FINAL,
    SET_FULL_SCREEN,
    SET_TOOLBOX_TIMEOUT,
    SET_WHITEBOARD_INSTANCE,
    TOGGLE_ETHERPAD
} from './actionTypes';
import { actionToggleEtherPad, actionToggleWhiteBoard, openWhiteBoardFunction } from './actions.any';
import { setShareMenuVisible } from './actions.web';


declare var APP: Object;

const WHITEBOARD_COMMAND = 'WHITEBOARD_COMMAND';
const ETHERPAD_COMMIT = 'ETHERPAD_COMMIT';

/**
 * Middleware which intercepts Toolbox actions to handle changes to the
 * visibility timeout of the Toolbox.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(store => next => action => {
    const { getState } = store;
    const state = getState();
    const conference = getCurrentConference(state);

    switch (action.type) {
    case CLEAR_TOOLBOX_TIMEOUT: {
        const { timeoutID } = store.getState()['features/toolbox'];

        clearTimeout(timeoutID);
        break;
    }

    case SET_FULL_SCREEN:
        return _setFullScreen(next, action);

    case SET_TOOLBOX_TIMEOUT: {
        const { timeoutID } = store.getState()['features/toolbox'];
        const { handler, timeoutMS } = action;

        clearTimeout(timeoutID);
        action.timeoutID = setTimeout(handler, timeoutMS);

        break;
    }

    case SET_WHITEBOARD_INSTANCE: {
        if (navigator.product === 'ReactNative') {
            store.dispatch(setShareMenuVisible(!getState()['features/toolbox'].visibility));
        }

        if (getMobileOperatingSystem() === 'ios') {
            // console.log('ios is in progress');
        } else {
            store.dispatch(toggleScreensharing(true));
        }
        const participants = state['features/base/participants'];
        const participant = participants.filter(i => i.local === true);

        if (participants.length === 1) {

            store.dispatch(openWhiteBoardFunction(action.open));

            if (action.open) {
                store.dispatch(actionToggleWhiteBoard(true));
            } else {
                conference.sendCommand(WHITEBOARD_COMMAND, {
                    attributes: {
                        value: 'false',
                        partId: null
                    }
                });
            }
        } else {
            conference.sendCommand(WHITEBOARD_COMMAND, {
                attributes: { value: action.open ? 'true' : 'false',
                    partId: action.open ? participant[0].id : null
                }
            });
        }

        break;
    }
    case TOGGLE_ETHERPAD: {
        if (conference) {
            if (getMobileOperatingSystem() === 'ios') {
                // console.log('ios is in progress');
            } else {
                store.dispatch(toggleScreensharing(true));
            }
            const participants = state['features/base/participants'];
            const participant = participants.filter(i => i.local === true);

            if (participants.length === 1) {
                store.dispatch(openEtherPadFunction(action.bool));

                if (action.bool) {
                    store.dispatch(actionToggleEtherPad(true));
                } else {
                    conference.sendCommand(ETHERPAD_COMMIT, {
                        attributes: {
                            value: 'false',
                            partId: null
                        }
                    });
                }
            } else {
                conference.sendCommand(ETHERPAD_COMMIT, {
                    attributes: { value: action.bool ? 'true' : 'false',
                        partId: action.bool ? participant[0].id : null
                    }
                });
            }
        }
        break;
    }
    case 'OPEN_ETHERPAD_FINAL': {
        const participants = state['features/base/participants'];
        const etherpad = state['features/etherpad'];
        const participant = participants.filter(i => i.local === true);

        conference.sendCommand(ETHERPAD_COMMIT, {
            attributes: { value: etherpad.openEtherPad ? 'true' : 'false',
                partId: etherpad.openEtherPad ? participant[0].id : null
            }
        });
        break;
    }

    case OPEN_WHITE_BOARD_FINAL: {
        const participants = state['features/base/participants'];
        const toolbox = state['features/toolbox'];
        const participant = participants.filter(i => i.local === true);

        conference.sendCommand(WHITEBOARD_COMMAND, {
            attributes: { value: toolbox.openWhiteBoard ? 'true' : 'false',
                partId: toolbox.openWhiteBoard ? participant[0].id : null
            }
        });
        break;
    }
    case OPEN_CARMODE_MODAL: {
        store.dispatch(setActiveModalId(CARMODE_MODAL_ID));
        break;
    }

    case CLOSE_CARMODE_MODAL: {
        store.dispatch(setActiveModalId());
        break;
    }
    }

    return next(action);
});

type DocumentElement = {
    +requestFullscreen?: Function,
    +mozRequestFullScreen?: Function,
    +webkitRequestFullscreen?: Function
}

/**
 * Makes an external request to enter or exit full screen mode.
 *
 * @param {Dispatch} next - The redux dispatch function to dispatch the
 * specified action to the specified store.
 * @param {Action} action - The redux action SET_FULL_SCREEN which is being
 * dispatched in the specified store.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _setFullScreen(next, action) {
    if (typeof APP === 'object') {
        const { fullScreen } = action;

        if (fullScreen) {
            const documentElement: DocumentElement
                = document.documentElement || {};

            if (typeof documentElement.requestFullscreen === 'function') {
                documentElement.requestFullscreen();
            } else if (
                typeof documentElement.mozRequestFullScreen === 'function') {
                documentElement.mozRequestFullScreen();
            } else if (
                typeof documentElement.webkitRequestFullscreen === 'function') {
                documentElement.webkitRequestFullscreen();
            }
        } else {
            /* eslint-disable no-lonely-if */

            // $FlowFixMe
            if (typeof document.exitFullscreen === 'function') {
                document.exitFullscreen();

            // $FlowFixMe
            } else if (typeof document.mozCancelFullScreen === 'function') {
                document.mozCancelFullScreen();

            // $FlowFixMe
            } else if (typeof document.webkitExitFullscreen === 'function') {
                document.webkitExitFullscreen();
            }

        }
    }

    return next(action);
}

/**
 * Set up state change listener to perform maintenance tasks when the conference
 * is left or failed, e.g. Clear messages or close the chat modal if it's left
 * open.
 */
StateListenerRegistry.register(
    state => getCurrentConference(state),
    (conference, store, previousConference) => {
        if (conference && conference !== previousConference) {
            conference.addCommandListener(WHITEBOARD_COMMAND,
                ({ attributes }) => {
                    const { dispatch, getState } = store;
                    const state = getState();
                    const stateMent = getState()['features/genericiframe'].visible;

                    // const _tileViewEnabled = shouldDisplayTileView(getState());
                    const { value, partId } = attributes;
                    const toolbox = state['features/toolbox'];
                    const participants = state['features/base/participants'];
                    const participant = participants.filter(i => i.local === true)[0];

                    // const participantID = participants.filter(i => i.id === partId)[0];


                    // if (value === 'true') {
                    //     store.dispatch(setTileView(false));
                    // } else {
                    //     store.dispatch(setTileView(!_tileViewEnabled));
                    // }
                    const localVideo = getLocalVideoTrack(state['features/base/tracks']);


                    store.dispatch(checkParticipant(partId));

                    if (value === 'true' && stateMent !== true) {
                        if (partId === participant.id) {
                            if (localVideo && localVideo.videoType === 'desktop' && !localVideo.muted) {
                                dispatch(toggleVisibility());

                                if (!toolbox.actionToggleWhiteBoard) {
                                    dispatch(actionToggleWhiteBoard(true));
                                }
                            }


                        }

                    } else if (value === 'false' && stateMent === true) {
                        dispatch(toggleVisibility());
                        dispatch(actionToggleWhiteBoard(false));
                        if (localVideo && localVideo.videoType === 'desktop' && !localVideo.muted) {
                            dispatch(toggleScreensharing(false));
                        }

                    }
                }
            );
        }
    });

StateListenerRegistry.register(
        state => getCurrentConference(state),
        (conference, store, previousConference) => {
            if (conference && conference !== previousConference) {
                conference.addCommandListener(ETHERPAD_COMMIT, ({ attributes }) => {
                    const { dispatch, getState } = store;
                    const etherpad = getState()['features/etherpad'];
                    const { editing: stateMentediting, onEdit } = etherpad;

                    const { value, partId } = attributes;
                    const participants = getState()['features/base/participants'];
                    const participant = participants.filter(i => i.local === true)[0];

                    store.dispatch(whichParticipant(partId));
                    const localVideo = getLocalVideoTrack(getState()['features/base/tracks']);

                    if (value === 'true' && stateMentediting !== true) {

                        if (partId === participant.id) {
                            if (localVideo && localVideo.videoType === 'desktop' && !localVideo.muted) {
                                dispatch(toggleDocument());
                                dispatch(toggleDocumentedit(!onEdit));

                                if (!etherpad.actionToggleEtherPad) {
                                    dispatch(actionToggleEtherPad(true));
                                }

                            }


                        }


                    } else if (value === 'false' && stateMentediting === true) {


                        dispatch(toggleDocument());
                        dispatch(toggleDocumentedit(!onEdit));
                        dispatch(actionToggleEtherPad(false));

                        if (localVideo && localVideo.videoType === 'desktop' && !localVideo.muted) {

                            dispatch(toggleScreensharing(false));
                        }


                    }
                });
            }

        }
);


/**
 * Determine the mobile operating system.
 * This function returns one of 'iOS', 'Android', 'Windows Phone', or 'unknown'.
 *
 * @returns {string}
 */
function getMobileOperatingSystem() {
    const platform1 = [ 'android', 'ios' ];


    for (let i = 0; i < platform1.length; i++) {

        if (navigator.userAgent.indexOf(platform1[i]) > -1) {

            return platform1[i];
        }
    }

    return 'nil';
}
